/**
 * Quantile classification for the district choropleth.
 *
 * Equal-interval classes were tried first and were useless: these metrics are
 * heavily skewed — a handful of districts carry most of the building stock — so
 * equal intervals strand almost everything in the palest class. Quantiles keep
 * every ramp step populated. The trade-off is unequal class widths, which is why
 * the legend prints the actual break values rather than "menos → más".
 */

/** Sequential ramp, one hue (brand warm, OKLCH h 37.7°), light → dark. */
export const SEQ = ["#f8ded7", "#f1b8a7", "#e68d72", "#d36341", "#ad411d"];

/** Neutral for a district with no value for the active metric. */
export const SEQ_NONE = "#d8d6cf";

/**
 * Four ascending class boundaries → five ramp classes.
 *
 * Returns fewer than four, or none at all, when the data cannot support them.
 * Callers must read the length rather than assume five classes.
 */
export function computeBreaks(
  values: readonly (number | null | undefined)[],
): number[] {
  const sorted = values
    .filter((x): x is number => x != null && Number.isFinite(x))
    .sort((a, b) => a - b);

  // Below five values a quantile split says nothing, and the caller paints one
  // flat class. Guarded here rather than at each call site.
  if (sorted.length < 5) return [];

  const at = (q: number) =>
    sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] as number;

  // MapLibre's 'step' requires strictly ascending stops, and ties are common
  // when a metric has many zeros. Duplicates are dropped rather than nudged
  // apart: inventing a boundary the data does not have would put an empty class
  // in the legend.
  const out: number[] = [];
  for (const b of [at(0.2), at(0.4), at(0.6), at(0.8)]) {
    if (out.length === 0 || b > (out[out.length - 1] as number)) out.push(b);
  }
  return out;
}

/**
 * The ramp for a metric, in ascending-value order — element 0 always paints the
 * lowest class.
 *
 * `lowFirst` reverses it so the darkest tone sits on the low end. Income is the
 * case that needs it: for every other metric a bigger number is the more notable
 * one, but the poorest districts are the ones to look at. Without this the page
 * contradicts itself — dark meaning "worst" on eight metrics and "best" on the
 * ninth.
 */
export function rampFor(breaks: readonly number[], lowFirst = false): string[] {
  const ramp = SEQ.slice(SEQ.length - (breaks.length + 1));
  return lowFirst ? [...ramp].reverse() : ramp;
}

/** The colour a single value lands on, given the active breaks. */
export function colorFor(
  value: number | null | undefined,
  breaks: readonly number[],
  lowFirst = false,
): string {
  if (value == null || !Number.isFinite(value)) return SEQ_NONE;

  let i = 0;
  while (i < breaks.length && value >= (breaks[i] as number)) i++;

  const ramp = rampFor(breaks, lowFirst);
  return ramp[i] ?? (ramp[ramp.length - 1] as string);
}
